import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import BulkDeleteModal from "../components/BulkDeleteModal";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (role) params.role = role;
      if (activeFilter === "true") params.is_active = true;
      if (activeFilter === "false") params.is_active = false;
      if (q) params.q = q;
      const { data } = await api.get("/admin/users", { params });
      setRows(data);
      setSelected({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  }, [role, activeFilter, q, t]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    if (u.role === "admin") return;
    const verb = u.is_active ? "suspend" : "reactivate";
    if (!window.confirm(u.is_active ? t("admin_users.confirm_suspend", { email: u.email }) : t("admin_users.confirm_reactivate", { email: u.email }))) return;
    try {
      await api.post(`/admin/users/${u.id}/${verb}`);
      toast.success(u.is_active ? t("admin_users.suspended") : t("admin_users.reactivated"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  };

  const isSelf = (u) => currentUser && u.id === currentUser.id;
  const selectableRows = useMemo(() => rows.filter((r) => !isSelf(r)), [rows, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedItems = useMemo(
    () => rows.filter((r) => selected[r.id]).map((r) => ({ id: r.id, label: `${r.email} (${r.role})` })),
    [rows, selected]
  );
  const allChecked = selectableRows.length > 0 && selectedIds.length === selectableRows.length;
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else {
      const next = {};
      selectableRows.forEach((r) => { next[r.id] = true; });
      setSelected(next);
    }
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids: selectedIds });
      toast.success(t("admin_users.bulk_deleted_toast", {
        users: data.deleted_users ?? 0,
        anon: data.anonymized_messages ?? 0,
      }));
      setBulkOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-users-page">
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_users.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-testid="admin-users-title">{t("admin_users.title")}</h1>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input placeholder={t("admin_users.search_placeholder")} value={q} onChange={(e) => setQ(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-users-q" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-users-role">
            <option value="">{t("admin_users.all_roles")}</option>
            <option value="employer">employer</option>
            <option value="agency">agency</option>
            <option value="admin">admin</option>
          </select>
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-users-active">
            <option value="">{t("admin_users.all_states")}</option>
            <option value="true">{t("admin_users.active")}</option>
            <option value="false">{t("admin_users.suspended_st")}</option>
          </select>
          {selectedIds.length > 0 && (
            <button
              onClick={() => setBulkOpen(true)}
              className="ml-auto rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              data-testid="admin-users-bulk-delete-btn"
            >
              {t("admin_bulk.delete_n_items", { count: selectedIds.length })}
            </button>
          )}
        </div>
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {loading ? <p className="text-sm text-slate-500 p-2">{t("common.loading")}</p> : rows.length === 0 ? (
            <p className="text-sm text-slate-500 p-2">{t("common.no_results")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 pl-1 w-8 text-left">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid="admin-users-select-all"
                    />
                  </th>
                  <th className="py-2 text-left">{t("admin_users.col_name")}</th>
                  <th className="py-2 text-left">{t("admin_users.col_email")}</th>
                  <th className="py-2 text-left">{t("admin_users.col_role")}</th>
                  <th className="py-2 text-left">{t("admin_users.col_org")}</th>
                  <th className="py-2 text-left">{t("admin_users.col_state")}</th>
                  <th className="py-2 text-left">{t("admin_users.col_created")}</th>
                  <th className="py-2 text-right">{t("admin_users.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const self = isSelf(u);
                  return (
                    <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-row-${u.id}`}>
                      <td className="py-2 pl-1">
                        <input
                          type="checkbox"
                          checked={!!selected[u.id]}
                          disabled={self}
                          onChange={() => toggleOne(u.id)}
                          title={self ? t("admin_users.cannot_select_self") : ""}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                          data-testid={`admin-user-select-${u.id}`}
                        />
                      </td>
                      <td className="py-2 text-slate-900">
                        {u.name}
                        {self && <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200">{t("admin_users.you_badge")}</span>}
                      </td>
                      <td className="py-2 text-slate-700">{u.email}</td>
                      <td className="py-2">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">{u.role}</span>
                      </td>
                      <td className="py-2 text-slate-500">{u.organization_name || "—"}</td>
                      <td className="py-2">
                        {u.is_active ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100" data-testid={`admin-user-state-${u.id}`}>{t("admin_users.active")}</span>
                        ) : (
                          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-100" data-testid={`admin-user-state-${u.id}`}>{t("admin_users.suspended_st")}</span>
                        )}
                      </td>
                      <td className="py-2 text-slate-500">{formatDateShort(u.created_at)}</td>
                      <td className="py-2 text-right">
                        {u.role !== "admin" && (
                          <button
                            onClick={() => toggle(u)}
                            className={
                              "rounded border px-2 py-0.5 text-xs font-semibold hover:bg-slate-50 " +
                              (u.is_active ? "border-rose-200 text-rose-700 hover:bg-rose-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50")
                            }
                            data-testid={`admin-user-toggle-${u.id}`}
                          >
                            {u.is_active ? t("admin_users.suspend") : t("admin_users.reactivate")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        <BulkDeleteModal
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onConfirm={doBulkDelete}
          busy={bulkBusy}
          items={selectedItems}
          kind="users"
          warning={t("admin_bulk.warning_users")}
        />
      </main>
    </div>
  );
}
